import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Users, ChevronDown, ChevronUp, Phone, RefreshCw, Smartphone, Search } from "lucide-react";
import { toast } from "sonner";

const API = '/api';

export default function WhatsAppGroups() {
  const [groups, setGroups] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProfile, setSelectedProfile] = useState("");
  const [expandedGroup, setExpandedGroup] = useState(null);
  const [search, setSearch] = useState("");

  const fetchGroups = useCallback(async () => {
    try {
      const params = {};
      if (selectedProfile) params.profile_id = selectedProfile;
      const res = await axios.get(`${API}/whatsapp/groups`, { params });
      setGroups(res.data);
    } catch (e) {
      // error silenced
      toast.error("Error al cargar grupos");
    } finally {
      setLoading(false);
    }
  }, [selectedProfile]);

  const fetchProfiles = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/profiles`);
      setProfiles(res.data.filter(p => p.type === "android"));
    } catch (e) {
      // error silenced
    }
  }, []);

  useEffect(() => { fetchProfiles(); }, [fetchProfiles]);
  useEffect(() => { fetchGroups(); }, [fetchGroups]);

  const filtered = groups.filter(g =>
    g.name.toLowerCase().includes(search.toLowerCase())
  );

  const getProfileName = (profileId) => {
    const p = profiles.find(pr => pr.id === profileId);
    return p ? p.name : profileId;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-green-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="wa-groups-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-white uppercase">
            Grupos WhatsApp
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            Grupos sincronizados desde la app Android
          </p>
        </div>
        <button
          data-testid="refresh-groups-btn"
          onClick={() => { setLoading(true); fetchGroups(); }}
          className="flex items-center gap-2 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-zinc-200 rounded-lg px-4 py-2 text-sm font-medium transition-colors self-start"
        >
          <RefreshCw className="w-4 h-4" />
          Actualizar
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <input
            data-testid="search-groups-input"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Buscar grupo..."
            className="w-full bg-zinc-950/50 border border-zinc-800 focus:border-green-500 focus:ring-1 focus:ring-green-500/20 rounded-md pl-10 pr-3 py-2 text-sm text-white placeholder:text-zinc-600 outline-none transition-colors"
          />
        </div>
        <select
          data-testid="filter-profile-select"
          value={selectedProfile}
          onChange={e => setSelectedProfile(e.target.value)}
          className="bg-zinc-950/50 border border-zinc-800 focus:border-green-500 rounded-md px-3 py-2 text-sm text-white outline-none transition-colors min-w-[180px]"
        >
          <option value="">Todos los perfiles</option>
          {profiles.map(p => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <div className="bg-zinc-900 border border-white/5 rounded-lg p-4">
          <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Grupos</p>
          <p className="text-2xl font-bold text-white mt-1">{filtered.length}</p>
        </div>
        <div className="bg-zinc-900 border border-white/5 rounded-lg p-4">
          <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Miembros Totales</p>
          <p className="text-2xl font-bold text-green-400 mt-1">
            {filtered.reduce((acc, g) => acc + (g.member_count || 0), 0)}
          </p>
        </div>
        <div className="bg-zinc-900 border border-white/5 rounded-lg p-4 col-span-2 sm:col-span-1">
          <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Perfiles Android</p>
          <p className="text-2xl font-bold text-blue-400 mt-1">{profiles.length}</p>
        </div>
      </div>

      {/* Groups list */}
      <div className="bg-zinc-900 border border-white/5 rounded-lg overflow-hidden">
        {filtered.length === 0 ? (
          <div className="text-center py-16">
            <Users className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
            <p className="text-zinc-500 text-sm">
              {groups.length === 0
                ? "No hay grupos sincronizados. Usa la app Android para extraer grupos."
                : "No se encontraron grupos con ese filtro."}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-white/5">
            {filtered.map((group, i) => (
              <div key={group.id || i} data-testid={`wa-group-row-${i}`}>
                {/* Group row */}
                <button
                  data-testid={`expand-group-${i}`}
                  onClick={() => setExpandedGroup(expandedGroup === i ? null : i)}
                  className="w-full flex items-center gap-4 px-4 py-3 hover:bg-white/[0.02] transition-colors text-left"
                >
                  <div className="w-10 h-10 rounded-full bg-green-600/15 border border-green-500/20 flex items-center justify-center flex-shrink-0">
                    <Users className="w-5 h-5 text-green-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-white truncate">{group.name}</p>
                    <div className="flex items-center gap-3 mt-0.5">
                      <span className="text-[10px] text-zinc-500 flex items-center gap-1">
                        <Smartphone className="w-3 h-3" />
                        {getProfileName(group.profile_id)}
                      </span>
                      <span className="text-[10px] text-zinc-600">
                        {group.updated_at ? new Date(group.updated_at).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <span className="bg-green-500/10 text-green-400 border border-green-500/20 rounded-full px-2.5 py-0.5 text-xs font-mono font-semibold">
                      {group.member_count || 0}
                    </span>
                    {expandedGroup === i
                      ? <ChevronUp className="w-4 h-4 text-zinc-500" />
                      : <ChevronDown className="w-4 h-4 text-zinc-500" />
                    }
                  </div>
                </button>

                {/* Expanded members */}
                {expandedGroup === i && (
                  <div className="bg-zinc-950/50 border-t border-white/5 px-4 py-3" data-testid={`group-members-${i}`}>
                    <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mb-2">
                      Miembros ({group.members?.length || 0})
                    </p>
                    {(!group.members || group.members.length === 0) ? (
                      <p className="text-xs text-zinc-600">Sin miembros registrados</p>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1.5 max-h-64 overflow-y-auto">
                        {group.members.map((m, mi) => (
                          <div key={mi} className="flex items-center gap-2 bg-zinc-900/60 rounded px-2.5 py-1.5">
                            <Phone className="w-3 h-3 text-zinc-600 flex-shrink-0" />
                            <div className="min-w-0 flex-1">
                              <p className="text-xs text-zinc-300 truncate">{m.name || "Sin nombre"}</p>
                              {m.phone && (
                                <p className="text-[10px] font-mono text-cyan-500/70">{m.phone}</p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
